/**
 * <OnboardingStepCard /> — one row in the /onboarding journey overview.
 */

import { Check, ChevronRight, CircleDot, SkipForward } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  STEP_EST_MINUTES,
  type StepSlug,
  type StepState,
} from "../../lib/onboarding";

export interface OnboardingStepCardProps {
  slug: StepSlug;
  index: number;
  state: StepState;
  isCurrent: boolean;
}

const _STEP_LABEL_KEYS: Record<StepSlug, string> = {
  welcome: "app.onb_step_welcome",
  company: "app.onb_step_company",
  policies: "app.onb_step_policies",
  "jobs-grades": "app.onb_step_jobs_grades",
  employees: "app.onb_step_employees",
  approvals: "app.onb_step_approvals",
  integrations: "app.onb_step_integrations",
  "shadow-payroll": "app.onb_step_shadow_payroll",
  "go-live": "app.onb_step_go_live",
};

const _STEP_DESC_KEYS: Record<StepSlug, string> = {
  welcome: "app.step_desc_welcome",
  company: "app.step_desc_company",
  policies: "app.step_desc_policies",
  "jobs-grades": "app.step_desc_jobs_grades",
  employees: "app.step_desc_employees",
  approvals: "app.step_desc_approvals",
  integrations: "app.step_desc_integrations",
  "shadow-payroll": "app.step_desc_shadow_payroll",
  "go-live": "app.step_desc_go_live",
};

const _STATUS_BADGE: Record<
  StepState["status"],
  { labelKey: string; className: string; icon: typeof Check }
> = {
  not_started: {
    labelKey: "app.step_card_not_started",
    className: "bg-surface-sunken text-text-tertiary border-border-subtle",
    icon: CircleDot,
  },
  in_progress: {
    labelKey: "app.step_card_in_progress",
    className: "bg-ai-bg text-ai-deep border-ai-subtle",
    icon: CircleDot,
  },
  completed: {
    labelKey: "app.step_card_completed",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: Check,
  },
  skipped: {
    labelKey: "app.step_card_skipped",
    className: "bg-amber-50 text-amber-700 border-amber-200",
    icon: SkipForward,
  },
};

export function OnboardingStepCard({
  slug,
  index,
  state,
  isCurrent,
}: OnboardingStepCardProps) {
  const { t } = useTranslation();
  const badge = _STATUS_BADGE[state.status];
  const Icon = badge.icon;
  const cta =
    state.status === "completed"
      ? t("app.step_card_cta_review")
      : state.status === "in_progress"
        ? t("app.step_card_cta_resume")
        : state.status === "skipped"
          ? t("app.step_card_cta_start_now")
          : isCurrent
            ? t("app.step_card_cta_continue")
            : t("app.step_card_cta_start");

  return (
    <article
      className={[
        "group relative overflow-hidden rounded-xl border bg-surface-default p-4 transition-shadow hover:shadow-sm",
        isCurrent ? "border-brand" : "border-border-subtle",
      ].join(" ")}
    >
      {isCurrent && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5 bg-brand-gradient-h"
        />
      )}
      <header className="flex items-start gap-3">
        <span
          className={[
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
            state.status === "completed"
              ? "bg-emerald-500 text-white"
              : isCurrent
                ? "bg-brand text-brand-fg"
                : "bg-surface-sunken text-text-secondary",
          ].join(" ")}
        >
          {state.status === "completed" ? (
            <Check size={14} aria-hidden />
          ) : (
            index + 1
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-text-primary">
              {t(_STEP_LABEL_KEYS[slug])}
            </h3>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs font-medium ${badge.className}`}
            >
              <Icon size={10} aria-hidden /> {t(badge.labelKey)}
            </span>
            <span className="ms-auto text-xs text-text-tertiary">
              {t("app.step_card_est_minutes", { n: STEP_EST_MINUTES[slug] })}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            {t(_STEP_DESC_KEYS[slug])}
          </p>
        </div>
      </header>
      <footer className="mt-3 flex items-center justify-end">
        <Link
          to={`/onboarding/${slug}`}
          className={[
            "inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            isCurrent || state.status === "in_progress"
              ? "bg-brand text-brand-fg hover:opacity-90"
              : "border border-border-subtle text-text-secondary hover:bg-surface-sunken",
          ].join(" ")}
        >
          {cta}
          <ChevronRight size={14} aria-hidden className="rtl:rotate-180" />
        </Link>
      </footer>
    </article>
  );
}
